
import { useState, useEffect, useRef } from 'react';

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Cancel any previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Only update the debounced value if the input value is valid
    if (value !== undefined && value !== null) {
      // Store the timeout ID for cleanup
      timeoutRef.current = setTimeout(() => {
        setDebouncedValue(value);
        timeoutRef.current = null;
      }, delay);
    }

    // Cleanup on unmount or when value/delay changes
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay]);

  // Return the debounced value
  return debouncedValue;
}

// Utility function to throttle API calls
export function useThrottle<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
  dependencies: any[] = []
): T {
  const lastCall = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const queuedArgsRef = useRef<Parameters<T> | null>(null);
  const inProgressRef = useRef<boolean>(false);
  const requestCountRef = useRef<number>(0);
  const maxRequestsPerMinute = useRef<number>(30); // Maximum requests per minute
  const requestTimeWindowRef = useRef<number[]>([]);

  // Use useRef to store the callback to avoid recreating the throttled function
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback, ...dependencies]);

  return ((...args: Parameters<T>) => {
    const now = Date.now();
    
    // Check request rate limiting
    const oneMinuteAgo = now - 60000;
    requestTimeWindowRef.current = requestTimeWindowRef.current.filter(time => time > oneMinuteAgo);
    
    if (requestTimeWindowRef.current.length >= maxRequestsPerMinute.current) {
      console.warn(`Request rate limit exceeded. Throttling for resource protection.`);
      return;
    }
    
    // Handle in-progress flag to prevent concurrent calls
    if (inProgressRef.current) {
      queuedArgsRef.current = args;
      return;
    }

    const timeSinceLastCall = now - lastCall.current;

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // If we can run the function now
    if (timeSinceLastCall >= delay) {
      lastCall.current = now;
      inProgressRef.current = true;
      requestTimeWindowRef.current.push(now);
      
      try {
        return callbackRef.current(...args);
      } finally {
        inProgressRef.current = false;
        
        // Process any queued calls
        if (queuedArgsRef.current) {
          const queuedArgs = queuedArgsRef.current;
          queuedArgsRef.current = null;
          
          timeoutRef.current = setTimeout(() => {
            timeoutRef.current = null;
            // Use the function with the queued arguments
            if (queuedArgs) {
              lastCall.current = Date.now();
              callbackRef.current(...queuedArgs);
            }
          }, delay);
        }
      }
    } 
    // Otherwise queue the call
    else {
      queuedArgsRef.current = args;
      
      timeoutRef.current = setTimeout(() => {
        if (queuedArgsRef.current) {
          lastCall.current = Date.now();
          inProgressRef.current = true;
          requestTimeWindowRef.current.push(Date.now());
          
          try {
            callbackRef.current(...queuedArgsRef.current);
          } finally {
            inProgressRef.current = false;
            queuedArgsRef.current = null;
            timeoutRef.current = null;
          }
        }
      }, delay - timeSinceLastCall);
    }
  }) as T;
}
